import React from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SectionCard from '../../../components/SectionCard';
import InvButton from '../../../components/InvButton';
import StatusChip from '../../../components/StatusChip';
import { IPD_COLORS } from '../../../theme';
import { formatDateTime } from '../../../utils';

/**
 * Stage of one controlled line, in the order the counter experiences it.
 * `chip` maps onto StatusChip's existing vocabulary.
 */
export const CONTROLLED_STAGES = {
  SELECT: { chip: 'DRAFT', label: 'Select batch & qty' },
  SENDING: { chip: 'PENDING', label: 'Sending to pharmacist' },
  PENDING: { chip: 'AWAITING_AUTHORIZATION', label: 'Parked with pharmacist' },
  AUTHORIZED: { chip: 'AUTHORIZED', label: 'Authorized' },
  DENIED: { chip: 'REJECTED', label: 'Denied' },
};

/**
 * Standing summary of every controlled line and where it currently sits.
 *
 * Without this the counter has no way to tell "not sent yet" from "sitting in
 * the pharmacist's queue" from "approved" — the payment button just stays
 * disabled with no explanation of what anyone is waiting for.
 */
export default function ControlledAuthStatusPanel({
  rows = [],
  loading = false,
  lastCheckedAt = null,
  onRefresh,
  onOpenQueue,
}) {
  if (!rows.length) return null;

  const waiting = rows.filter((r) => r.stage === 'PENDING').length;
  const authorized = rows.filter((r) => r.stage === 'AUTHORIZED').length;
  const denied = rows.filter((r) => r.stage === 'DENIED').length;

  let headline;
  if (denied) {
    headline = `${denied} line${denied > 1 ? 's' : ''} denied — remove or change ${denied > 1 ? 'them' : 'it'} to continue`;
  } else if (waiting) {
    headline = `${waiting} of ${rows.length} parked with the pharmacist — collect from Controlled Orders once authorized`;
  } else if (authorized === rows.length) {
    headline = 'All controlled medicines authorized — payment is unlocked';
  } else {
    headline = 'Preparing controlled medicines for the queue';
  }

  return (
    <SectionCard
      title="Controlled substance authorization"
      subtitle={headline}
      actions={(
        <Box sx={{ display: 'flex', gap: 1 }}>
          <InvButton
            size="small"
            variant="outlined"
            tone="teal"
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
            disabled={loading}
            onClick={onRefresh}
          >
            Check now
          </InvButton>
          <Tooltip title="Opens in a new tab so this dispense is not lost">
            <span>
              <InvButton
                size="small"
                variant="outlined"
                tone="teal"
                startIcon={<OpenInNewIcon />}
                onClick={onOpenQueue}
              >
                Controlled Queue
              </InvButton>
            </span>
          </Tooltip>
        </Box>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.map((row) => {
          const stage = CONTROLLED_STAGES[row.stage] || CONTROLLED_STAGES.SELECT;
          return (
            <Box
              key={row.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                p: 1.25,
                borderRadius: '10px',
                border: `1px solid ${IPD_COLORS.border}`,
              }}
            >
              <LockIcon fontSize="small" sx={{ color: IPD_COLORS.textSecondary }} />
              <Box sx={{ minWidth: 180, flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  {row.medicineName || 'Medicine'}
                </Typography>
                {row.qty ? (
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    Qty {row.qty}
                  </Typography>
                ) : null}
              </Box>
              <StatusChip status={stage.chip} label={stage.label} />
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, flexBasis: '100%' }}>
                {row.detail}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 1.5 }}>
        {lastCheckedAt
          ? `Last checked ${formatDateTime(lastCheckedAt)} · re-checks when you return to this tab`
          : 'Re-checks when you return to this tab'}
      </Typography>
    </SectionCard>
  );
}
